import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  Edit as EditIcon,
} from '@mui/icons-material';
import { pipelineApi, type Pipeline } from '@/api/pipeline';
import { PipelineEditModal } from './PipelineEditModal';

interface StreamPipelinesTabProps {
  streamId: string;
}

const normalizeImportRules = (value: Pipeline['import_rules'] | undefined) => {
  const defaultRules = {
    dont_import_open_to_work: false,
    dont_import_less_that_500_connections: false,
    dont_import_no_photo_on_profile: false,
    dont_import_free_linkedin_account: false,
    dont_import_duplicate_prospects_with_same_owner: true,
    delete_other_waiting_prospects_with_same_owner: false,
  };
  return {
    dont_import_open_to_work: value?.dont_import_open_to_work ?? defaultRules.dont_import_open_to_work,
    dont_import_less_that_500_connections:
      value?.dont_import_less_that_500_connections ?? defaultRules.dont_import_less_that_500_connections,
    dont_import_no_photo_on_profile:
      value?.dont_import_no_photo_on_profile ?? defaultRules.dont_import_no_photo_on_profile,
    dont_import_free_linkedin_account:
      value?.dont_import_free_linkedin_account ?? defaultRules.dont_import_free_linkedin_account,
    dont_import_duplicate_prospects_with_same_owner:
      value?.dont_import_duplicate_prospects_with_same_owner ??
      defaultRules.dont_import_duplicate_prospects_with_same_owner,
    delete_other_waiting_prospects_with_same_owner:
      value?.delete_other_waiting_prospects_with_same_owner ??
      defaultRules.delete_other_waiting_prospects_with_same_owner,
  };
};

const PipelineCard: React.FC<{ pipeline: Pipeline }> = ({ pipeline }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const importRules = normalizeImportRules(pipeline.import_rules);
  const enabledRuleKeys = Object.entries(importRules)
    .filter(([, value]) => value)
    .map(([key]) => key);

  const activeDays = pipeline.active_days || [];
  const activeDaysLabel = activeDays.length > 0 ? activeDays.join(', ') : 'Any day';

  const activeHoursFrom = pipeline.active_hours_from;
  const activeHoursTo = pipeline.active_hours_to;
  const activeHoursLabel =
    activeHoursFrom === undefined && activeHoursTo === undefined
      ? 'Any time'
      : `${activeHoursFrom ?? 0}:00 – ${activeHoursTo ?? 24}:00`;

  return (
    <>
      <Card>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                {pipeline.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                {pipeline.status && (
                  <Chip
                    size="small"
                    label={pipeline.status}
                    color={
                      pipeline.status === 'Failed'
                        ? 'error'
                        : pipeline.status === 'Done'
                        ? 'success'
                        : 'default'
                    }
                    variant="outlined"
                  />
                )}
                {pipeline.available_at && (
                  <Chip
                    size="small"
                    label={`Available: ${new Date(pipeline.available_at).toLocaleDateString()}`}
                    variant="outlined"
                    color="info"
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Edit pipeline settings">
                <span>
                  <IconButton size="small" onClick={() => setIsDialogOpen(true)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
            <Chip
              size="small"
              label={`Active days: ${activeDaysLabel}`}
              variant="outlined"
              color="primary"
            />
            <Chip
              size="small"
              label={`Hours: ${activeHoursLabel}`}
              variant="outlined"
              color="secondary"
            />
            <Chip
              size="small"
              label={
                enabledRuleKeys.length === 0
                  ? 'Import rules: default'
                  : `Import rules: ${enabledRuleKeys.length} enabled`
              }
              variant="outlined"
              color={enabledRuleKeys.length ? 'warning' : 'default'}
            />
          </Box>
        </CardContent>
      </Card>

      <PipelineEditModal
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        pipeline={pipeline}
      />
    </>
  );
};

export const StreamPipelinesTab: React.FC<StreamPipelinesTabProps> = ({ streamId }) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    // Use a distinct query key from the import modal to avoid
    // mixing paginated and non-paginated data shapes in React Query cache.
    queryKey: ['stream-pipelines', 'tab', streamId],
    queryFn: ({ pageParam }) =>
      pipelineApi.getPipelines({
        stream_uuid: streamId,
        page_token: pageParam,
        max_results: 20,
      }),
    getNextPageParam: (lastPage) => lastPage.output.next_page_token,
    initialPageParam: undefined as string | undefined,
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    const sentinel = sentinelRef.current;
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  const pipelines: Pipeline[] =
    data?.pages.flatMap((page) => page.output?.items || []) || [];

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load pipelines. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        Pipelines
      </Typography>

      {pipelines.length === 0 ? (
        <Alert severity="info">No pipelines created for this stream yet.</Alert>
      ) : (
        pipelines.map((pipeline) => <PipelineCard key={pipeline.key} pipeline={pipeline} />)
      )}

      <div ref={sentinelRef} style={{ height: 24 }}>
        {isFetchingNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </div>
    </Box>
  );
};


